import type { InternalMessage } from '../../MessagesV2.types';
import { deriveProviderLabel, deriveStatusMessage } from '../../MessagesV2.utils';

type CsvRow = Readonly<{
  csvDate: string;
  channel: string;
  provider: string;
  recipient: string;
  fromEmail: string;
  replyToEmail: string;
  messageType: string;
  attempt: number;
  rawStatus: string;
  statusMessage: string;
  sentForEventId: string;
  messageBatchId: string;
  stationeryOrderId: string;
}>;

/**
 * The export's columns. `key` is typed against `CsvRow`, so a header naming a field the builder does
 * not write — which would render as a silently empty column — fails to compile.
 */
export const CSV_HEADERS: Array<{ label: string; key: keyof CsvRow }> = [
  { label: 'Date', key: 'csvDate' },
  { label: 'Channel', key: 'channel' },
  { label: 'Provider', key: 'provider' },
  { label: 'Recipient', key: 'recipient' },
  { label: 'From email', key: 'fromEmail' },
  { label: 'Reply-to', key: 'replyToEmail' },
  { label: 'Message type', key: 'messageType' },
  { label: 'Attempt', key: 'attempt' },
  { label: 'Last status', key: 'rawStatus' },
  { label: 'Status message', key: 'statusMessage' },
  { label: 'Event ID', key: 'sentForEventId' },
  { label: 'Batch ID', key: 'messageBatchId' },
  { label: 'Card order ID', key: 'stationeryOrderId' },
];

/**
 * Flattens the rows behind the table into the export.
 *
 * Unfolded on purpose: the table shows one row per message, and this stays the full per-attempt
 * record, which is why `attempt` is worth a column here. A plain integer rather than "2 of 3" so a
 * spreadsheet can sort and filter on it.
 *
 * `createAt` is the GraphQL `Date!` scalar (an object with `.milliseconds`) — serialize to ISO.
 *
 * Attempt numbers are passed in rather than derived here, and deliberately: they have to be counted
 * over the whole page, while the rows exported are the filtered ones. Deriving them from `messages`
 * alone would number a group by the subset that survived the filters, so a third attempt exported
 * as 2 whenever a status filter hid the earlier two.
 */
export function buildCsvRows(messages: readonly InternalMessage[], attemptNumberById: ReadonlyMap<string, number>): CsvRow[] {
  return messages.map(m => ({
    csvDate: new Date(m.createAt.milliseconds).toISOString(),
    channel: m.type,
    // Blank on email, matching the table. Lossless here too: `channel` is the column beside it, and
    // every email row's provider is `mailgun`.
    provider: deriveProviderLabel(m),
    recipient: m.recipient.email || m.recipient.phoneNumber || '',
    fromEmail: m.fromEmail ?? '',
    replyToEmail: m.replyToEmail ?? '',
    messageType: m.messageType ?? '',
    attempt: attemptNumberById.get(m.id) ?? 1,
    rawStatus: m.rawStatus,
    statusMessage: deriveStatusMessage(m),
    sentForEventId: m.sentForEventId ?? '',
    messageBatchId: m.messageBatchId ?? '',
    stationeryOrderId: m.stationeryOrderId ?? '',
  }));
}
